import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import { useStore } from "@tanstack/react-form";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { Option } from "api/common/types/common.types";
import { useAppForm } from "components/Forms/AppForm";
import TestedFeaturesSection, {
  FeatureData,
} from "components/Forms/components/sections/TestedFeaturesSection/TestedFeaturesSection";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";
import { standardDateFormat } from "utils/timeUtils";

import {
  PcmFormValidationSchema,
  PcmFormValidationSchemaEdit,
} from "./ProposeInternationalProduct.schema";
import AdditionalInfoSection from "./sections/AdditionalInfoSection";
import LabSelectSection from "./sections/LabSelectSection";
import MaintenanceCheckboxSection from "./sections/MaintenanceCheckboxSection";
import MaintenanceSelectSection from "./sections/MaintenanceSelectSection";
import PcmDetailsSection from "./sections/PcmDetailsSection";
import PcmEditFieldsSection from "./sections/PcmEditFieldsSection";
import PcmFileUploadSection, {
  PcmFileMetadata,
} from "./sections/PcmFileUploadSection";
import { pcmFormDefaultValues } from "./sections/pcmFormDefaultValues";
import PcmPPSection from "./sections/PcmPPSection";
import ProductSelectSection from "./sections/ProductSelectSection";
import SchemeSelectSection from "./sections/SchemeSelectSection";
import { usePcmCascadingData } from "./usePcmCascadingData";
import { usePcmFormData } from "./usePcmFormData";
import { usePcmFormMutations } from "./usePcmFormMutations";

interface AlertType {
  message: string;
  type: string;
}

interface ProposeInternationalProductModalProps {
  show: boolean;
  onClose: () => void;
  setAlert?: (_alert: AlertType) => void;
  refetch?: () => void;
  pcmRid?: number | null;
}

const ProposeInternationalProductModal = ({
  show,
  onClose,
  setAlert,
  refetch,
  pcmRid,
}: ProposeInternationalProductModalProps) => {
  const [error, setError] = useState<string>("");

  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();
  const { currentUser } = useAuth();
  // User is authenticated if their ID is not the default -1
  const isAuthenticated = currentUser?.id !== -1;
  const isEditMode = !!pcmRid;

  // reCAPTCHA
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const {
    triggerCreatePcm,
    triggerUpdatePcm,
    buildPcmPayload,
    buildUpdatePayload,
    handleFileUpload,
    handleFeatureChanges,
  } = usePcmFormMutations();

  // Fetch initial/static data (we need pcm data to populate form defaults)
  const {
    pcm,
    schemes,
    ppOptions,
    pcmPpOptions,
    existingFeatures,
    isLoading: _isLoading,
  } = usePcmFormData(pcmRid ?? undefined);

  // Store original feature IDs for update comparison
  const originalFeatureIds = existingFeatures.map((f) => f.id);

  const handlePostSuccess = (
    rid: number,
    testedFeatures: FeatureData[],
    files: PcmFileMetadata[],
    successMessage: string,
  ) => {
    if (files.length > 0) {
      handleFileUpload(rid, files);
    }

    if (testedFeatures.length > 0) {
      handleFeatureChanges(
        isEditMode ? originalFeatureIds : [],
        testedFeatures,
        rid,
      );
    }

    hideLoadingSpinner();
    onClose();

    if (setAlert) {
      setAlert({
        message: successMessage,
        type: "success",
      });
    }
    if (refetch) {
      refetch();
    }
  };

  const isDefaultMaintenance = pcm?.maintenance_id != null;
  const defaultTestedFeatures: FeatureData[] = existingFeatures.map((f) => ({
    id: f.id,
    feature: f.feature,
    feature_name: f.feature_name,
  }));
  // TODO: FIX TYPES
  // Compute default values from PCM data (works for both create and edit)
  const defaultValues = {
    ...pcmFormDefaultValues,
    ...(isDefaultMaintenance
      ? { isMaintenance: true as const }
      : { isMaintenance: false as const }),
    scheme: {
      label: pcm?.nsid?.cid.name || "",
      value: pcm?.nsid?.cid.code2_iso || "",
    },
    ccProduct: {
      label: pcm?.ccid?.name || "",
      value: pcm?.ccid?.ccid || "",
    },
    ccMaintenance: {
      label: pcm?.maintenance_id?.name || "",
      value: pcm?.maintenance_id?.maintenance_id || 0,
    },
    ccLab: {
      label: pcm?.assigned_lab?.cctl_name || "",
      value: pcm?.assigned_lab?.cctl_id || 0,
    },
    phone1: pcm?.phone1 ?? "",
    custemail: pcm?.custemail ?? "",
    custuname: pcm?.custuname ?? "",
    vendor: pcm?.vendor_id?.name ?? "",
    protectionProfiles: pcmPpOptions ?? ([] as Option[]),
    // files: [] as PcmFileMetadata[], // TODO: Populate from existing files when file fetching is added
    additionalInformation: pcm?.additional_information ?? "",
    testedFeatures: defaultTestedFeatures,
    // Edit mode fields
    deadline: pcm?.deadline ? standardDateFormat(pcm.deadline) : "",
  };

  const pcmForm = useAppForm({
    defaultValues,
    validators: {
      onSubmit: isEditMode
        ? PcmFormValidationSchemaEdit
        : PcmFormValidationSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        setError("");

        // Validate reCAPTCHA for unauthenticated users (create mode only)
        if (!isAuthenticated && !isEditMode) {
          if (!recaptchaToken) {
            setError("Please complete the reCAPTCHA");

            return;
          }
        }

        // Get reCAPTCHA token
        const recaptchaSubmissionToken = isAuthenticated
          ? "member_portal"
          : recaptchaToken || "";

        const scheme = schemes.find(
          (s) => s.cid__code2_iso === value.scheme.value,
        )!;

        const product = ccProducts.find(
          (p) => p.ccid === value.ccProduct.value,
        )!;

        const ccMaintenance = ccMaintenances.find(
          (ccm) => ccm.maintenance_id === value.ccMaintenance.value,
        );

        if (isEditMode && pcmRid) {
          // Update existing PCM
          showLoadingSpinner("Updating PCM...");

          const payload = buildUpdatePayload({
            value,
            pcmRid,
            recaptchaToken: recaptchaSubmissionToken,
            product,
            scheme,
          });
          await triggerUpdatePcm(payload).unwrap();

          handlePostSuccess(
            pcmRid,
            value.testedFeatures,
            value.files,
            "PCM Updated!",
          );
        } else {
          // Create new PCM
          showLoadingSpinner("Submitting PCM...");
          const payload = buildPcmPayload({
            value,
            recaptchaToken: recaptchaSubmissionToken,
            product,
            scheme,
            ccMaintenance,
          });
          const response = await triggerCreatePcm(payload).unwrap();

          // Format success message with PCM ticket
          const ticketLabel = `PCM-${moment.utc(response.entrydate).format("YYYY")}-${response.ticket}`;

          handlePostSuccess(
            response.rid,
            value.testedFeatures,
            value.files,
            `Proposal of International Product Successfully Submitted as ${ticketLabel}!`,
          );
        }
      } catch (err) {
        hideLoadingSpinner();
        setError(
          isEditMode
            ? "Error Updating PCM."
            : "Error Submitting Proposal of International Product.",
        );
      }
    },
    canSubmitWhenInvalid: true,
    onSubmitInvalid: async () => {
      setError("Please fill out all required fields before submitting");
    },
  });

  const { value: schemeCode } = useStore(
    pcmForm.store,
    (state) => state.values.scheme,
  );
  const isMaintenance = useStore(
    pcmForm.store,
    (state) => state.values.isMaintenance,
  );
  const { value: ccid } = useStore(
    pcmForm.store,
    (state) => state.values.ccProduct,
  );

  // Fetch cascading data (products, labs, maintenances) reactively based on form state
  const { ccProducts, ccLabs, ccMaintenances, isFetchingCascadingData } =
    usePcmCascadingData({
      pcmRid: pcmRid ?? undefined,
      schemeCode,
      isMaintenance,
      ccid,
    });

  // Show loading spinner when cascading data is being fetched (only when modal is open)
  useEffect(() => {
    if (show && isFetchingCascadingData) {
      showLoadingSpinner("Loading form options...");
    } else {
      hideLoadingSpinner();
    }
  }, [show, isFetchingCascadingData, showLoadingSpinner, hideLoadingSpinner]);

  return (
    <Dialog
      open={show}
      fullScreen
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
        hideLoadingSpinner();
      }}
      sx={{
        padding: "1em",
        margin: 0,
      }}
      slotProps={{
        paper: {
          sx: {
            margin: "64px",
            "&.MuiDialog-paperScrollBody": {
              maxWidth: "900px",
            },
          },
        },
      }}
      maxWidth="md"
      scroll="body"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogTitle>
          <Box textAlign="center" mt={2}>
            <Typography variant="h4">
              {isEditMode
                ? `EDIT PRODUCT CONCURRENCE MANAGEMENT REQUEST - ${pcm?.ticket}`
                : "+ PROPOSE AN INTERNATIONAL PRODUCT FOR THE PCL"}
            </Typography>
          </Box>
        </DialogTitle>
        <Divider
          variant="fullWidth"
          sx={{ opacity: ".25", borderColor: "black" }}
        />
        <DialogContent>
          {!isEditMode && (
            <Box textAlign="center" mt={2}>
              <Typography variant="body1">
                NIAP International Posting
              </Typography>
              <Typography variant="body1">
                {" "}
                Please use the form below to propose the inclusion of an
                international product on the PCL.
              </Typography>
              <Typography variant="body1">
                PLEASE FILL OUT ALL THE INFORMATION BELOW
              </Typography>
            </Box>
          )}
          <MaintenanceCheckboxSection form={pcmForm} />
          <SchemeSelectSection form={pcmForm} schemes={schemes} />
          <ProductSelectSection form={pcmForm} ccProducts={ccProducts} />
          <pcmForm.Subscribe selector={(state) => state.values.isMaintenance}>
            {(isMaintenance) =>
              isMaintenance && (
                <MaintenanceSelectSection
                  form={pcmForm}
                  ccMaintenances={ccMaintenances}
                />
              )
            }
          </pcmForm.Subscribe>
          <LabSelectSection form={pcmForm} ccLabs={ccLabs} />
          <pcmForm.Subscribe selector={(state) => state.values.vendor}>
            {(vendorName) => (
              <PcmDetailsSection form={pcmForm} vendorName={vendorName ?? ""} />
            )}
          </pcmForm.Subscribe>
          <PcmPPSection form={pcmForm} ppOptions={ppOptions} />
          <TestedFeaturesSection form={pcmForm} fieldName="testedFeatures" />
          {!isEditMode && (
            <pcmForm.Subscribe selector={(state) => state.values.isMaintenance}>
              {(isMaintenance) => (
                <PcmFileUploadSection
                  form={pcmForm}
                  isMaintenance={isMaintenance}
                />
              )}
            </pcmForm.Subscribe>
          )}
          <AdditionalInfoSection form={pcmForm} />

          {/* reCAPTCHA for unauthenticated users */}
          {!isAuthenticated && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
              onChange={(token: string | null) => {
                setRecaptchaToken(token);
              }}
              onExpired={() => setRecaptchaToken(null)}
            />
          )}

          {/* Edit mode fields */}
          {isEditMode && <PcmEditFieldsSection form={pcmForm} />}

          {error && (
            <Alert color="error" variant="standard" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            flexWrap="wrap"
            mt={4}
          >
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                pcmForm.handleSubmit();
              }}
            >
              {isEditMode ? "Save" : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default ProposeInternationalProductModal;
